import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Menu, X, User, LogOut, Sparkles } from 'lucide-react';

export function Navbar() {
  const { t } = useTranslation();
  const { user, isAuthenticated, logout } = useAuthStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 glass border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
              <Sparkles className="h-8 w-8 text-gold" style={{ color: '#D4A574' }} />
              <span className="font-serif text-2xl font-bold text-primary">Tantra Connect</span>
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/services" className="text-secondary hover:text-gold transition-colors" data-testid="nav-services">{t('nav.services')}</Link>
            <Link to="/courses" className="text-secondary hover:text-gold transition-colors" data-testid="nav-courses">{t('nav.courses')}</Link>
            <Link to="/teachers" className="text-secondary hover:text-gold transition-colors" data-testid="nav-teachers">{t('nav.teachers')}</Link>
            <Link to="/schools" className="text-secondary hover:text-gold transition-colors" data-testid="nav-schools">{t('nav.schools')}</Link>

            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                {(user?.role === 'admin' || user?.roles?.includes('admin')) && (
                  <Link to="/admin" className="text-gold font-medium" data-testid="nav-admin">{t('nav.admin')}</Link>
                )}
                <Link to="/profile" className="flex items-center space-x-2 text-secondary hover:text-gold" data-testid="nav-profile">
                  <User className="h-5 w-5" />
                  <span>{user?.name}</span>
                </Link>
                <button onClick={handleLogout} className="text-secondary hover:text-gold" data-testid="logout-btn">
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Link to="/login" data-testid="nav-login">
                  <Button variant="ghost" className="text-secondary">{t('nav.login')}</Button>
                </Link>
                <Link to="/register" data-testid="nav-register">
                  <Button className="bg-gold hover:bg-gold/90 text-white" style={{ backgroundColor: '#D4A574' }}>{t('nav.register')}</Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="flex md:hidden items-center">
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-primary p-2" data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <div className="px-4 py-4 space-y-3">
            <Link to="/services" className="block py-2 text-secondary" onClick={() => setMobileMenuOpen(false)}>{t('nav.services')}</Link>
            <Link to="/courses" className="block py-2 text-secondary" onClick={() => setMobileMenuOpen(false)}>{t('nav.courses')}</Link>
            <Link to="/teachers" className="block py-2 text-secondary" onClick={() => setMobileMenuOpen(false)}>{t('nav.teachers')}</Link>
            <Link to="/schools" className="block py-2 text-secondary" onClick={() => setMobileMenuOpen(false)}>{t('nav.schools')}</Link>
            {isAuthenticated ? (
              <>
                {(user?.role === 'admin' || user?.roles?.includes('admin')) && <Link to="/admin" className="block py-2 text-gold font-medium" onClick={() => setMobileMenuOpen(false)}>{t('nav.admin')}</Link>}
                <Link to="/profile" className="block py-2 text-secondary" onClick={() => setMobileMenuOpen(false)}>{t('nav.profile')}</Link>
                <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="block py-2 text-secondary">{t('nav.logout')}</button>
              </>
            ) : (
              <>
                <Link to="/login" className="block py-2 text-secondary" onClick={() => setMobileMenuOpen(false)}>{t('nav.login')}</Link>
                <Link to="/register" className="block py-2 text-gold font-medium" onClick={() => setMobileMenuOpen(false)}>{t('nav.register')}</Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
